import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { recipeService } from '../../services/api';
import { 
  FiPlus, FiSearch, FiClock, FiUsers, FiEdit2, 
  FiTrash2, FiArrowLeft, FiX, FiBook
} from 'react-icons/fi';
import './Recipes.css';

const CATEGORIES = [
  'Appetizer', 'Breakfast', 'Lunch', 'Dinner', 'Side Dish',
  'Dessert', 'Drink', 'Snack', 'Sauce', 'Bread', 'Soup', 'Salad', 'Other'
];

const Recipes = () => {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const fetchRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await recipeService.getAllRecipes(
        selectedCategory || null,
        searchQuery || null
      );
      if (response.success) {
        setRecipes(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch recipes:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const location = useLocation();

  useEffect(() => {
    setSelectedRecipe(null);
    setShowCreateForm(false);
    setShowEditForm(false);
  }, [location.key]);

  const handleViewRecipe = async (recipeId) => {
    try {
      setLoadingRecipe(true);
      const response = await recipeService.getRecipe(recipeId);
      if (response.success) {
        setSelectedRecipe(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch recipe:', err);
    } finally {
      setLoadingRecipe(false);
    }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    try {
      const response = await recipeService.deleteRecipe(recipeId);
      if (response.success) {
        setRecipes(prev => prev.filter(r => r.recipeID !== recipeId));
        if (selectedRecipe?.recipeID === recipeId) {
          setSelectedRecipe(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete recipe:', err);
    }
  };

  const handleRecipeCreated = (newRecipe) => {
    setRecipes(prev => [newRecipe, ...prev]);
    setShowCreateForm(false);
    setSelectedRecipe(newRecipe);
  };

  const handleRecipeUpdated = (updatedRecipe) => {
    setRecipes(prev => prev.map(r => r.recipeID === updatedRecipe.recipeID ? updatedRecipe : r));
    setSelectedRecipe(updatedRecipe);
    setShowEditForm(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecipes();
  };

  const formatTime = (minutes) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs} hr ${mins} min` : `${hrs} hr`;
  };

  const canEdit = (recipe) => {
    return user?.memberID === recipe.createdByMemberID || user?.isAdmin;
  };

  // Detail View
  if (selectedRecipe && !showEditForm) {
    return (
      <div className="recipes-page">
        <div className="container">
          <button className="back-btn" onClick={() => { setSelectedRecipe(null); fetchRecipes(); }}>
            <FiArrowLeft /> Back to Recipes
          </button>

          <div className="recipe-detail card">
            <div className="card-body">
              {selectedRecipe.photoURL && (
                <div className="recipe-detail-photo">
                  <img src={selectedRecipe.photoURL} alt={selectedRecipe.title} />
                </div>
              )}

              <div className="recipe-detail-header">
                <h1>{selectedRecipe.title}</h1>
                {selectedRecipe.description && (
                  <p className="recipe-detail-description">{selectedRecipe.description}</p>
                )}

                <div className="recipe-detail-meta">
                  {selectedRecipe.prepTime && (
                    <span className="recipe-meta-item">
                      <FiClock /> Prep: {formatTime(selectedRecipe.prepTime)}
                    </span>
                  )}
                  {selectedRecipe.cookTime && (
                    <span className="recipe-meta-item">
                      <FiClock /> Cook: {formatTime(selectedRecipe.cookTime)}
                    </span>
                  )}
                  {selectedRecipe.totalTime && (
                    <span className="recipe-meta-item">
                      <FiClock /> Total: {formatTime(selectedRecipe.totalTime)}
                    </span>
                  )}
                  {selectedRecipe.servings && (
                    <span className="recipe-meta-item">
                      <FiUsers /> Serves {selectedRecipe.servings}
                    </span>
                  )}
                  {selectedRecipe.category && (
                    <span className="recipe-meta-badge">{selectedRecipe.category}</span>
                  )}
                </div>

                {canEdit(selectedRecipe) && (
                  <div className="recipe-detail-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowEditForm(true)}>
                      <FiEdit2 /> Edit
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(selectedRecipe.recipeID)}>
                      <FiTrash2 /> Delete
                    </button>
                  </div>
                )}
              </div>

              <div className="recipe-detail-body">
                <div className="recipe-section">
                  <h3>Ingredients</h3>
                  <div className="recipe-ingredients">
                    {selectedRecipe.ingredients.split('\n').map((line, i) => (
                      line.trim() && <div key={i} className="ingredient-line">{line}</div>
                    ))}
                  </div>
                </div>

                <div className="recipe-section">
                  <h3>Instructions</h3>
                  <div className="recipe-instructions">
                    {selectedRecipe.instructions.split('\n').map((line, i) => (
                      line.trim() && <div key={i} className="instruction-step">{line}</div>
                    ))}
                  </div>
                </div>

                {selectedRecipe.originStory && (
                  <div className="recipe-section">
                    <h3>Origin Story</h3>
                    <p className="recipe-origin">{selectedRecipe.originStory}</p>
                  </div>
                )}

                {selectedRecipe.originalCreatorName && (
                  <div className="recipe-section">
                    <h3>Original Creator</h3>
                    <p>{selectedRecipe.originalCreatorName}</p>
                  </div>
                )}
              </div>

              <div className="recipe-detail-footer">
                <span>Added by {selectedRecipe.createdByMember?.fullName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit Form
  if (showCreateForm || showEditForm) {
    return (
      <div className="recipes-page">
        <div className="container">
          <RecipeForm
            recipe={showEditForm ? selectedRecipe : null}
            onSave={showEditForm ? handleRecipeUpdated : handleRecipeCreated}
            onCancel={() => {
              setShowCreateForm(false);
              setShowEditForm(false);
            }}
          />
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="recipes-page">
      <div className="container">
        <div className="page-header">
          <h1><FiBook className="page-header-icon" /> Family Recipes</h1>
          <p className="page-subtitle">Cherished family recipes passed down through generations</p>
        </div>

        <div className="recipes-toolbar">
          <form className="recipes-search" onSubmit={handleSearch}>
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="form-input"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button type="button" className="search-clear" onClick={() => { setSearchQuery(''); }}>
                  <FiX />
                </button>
              )}
            </div>
          </form>

          <select
            className="form-input category-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            <FiPlus /> Add Recipe
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading recipes...</p>
          </div>
        ) : recipes.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">🍳</div>
            <h3 className="empty-state-title">No recipes yet</h3>
            <p>Be the first to share a family recipe!</p>
            <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
              <FiPlus /> Add Recipe
            </button>
          </div>
        ) : (
          <div className="recipes-grid">
            {recipes.map(recipe => (
              <div
                key={recipe.recipeID}
                className="recipe-card card"
                onClick={() => handleViewRecipe(recipe.recipeID)}
              >
                <div className="recipe-card-photo">
                  {recipe.photoURL ? (
                    <img src={recipe.photoURL} alt={recipe.title} />
                  ) : (
                    <div className="recipe-card-placeholder">🍽️</div>
                  )}
                  {recipe.category && (
                    <span className="recipe-card-badge">{recipe.category}</span>
                  )}
                </div>
                <div className="recipe-card-info">
                  <h3 className="recipe-card-title">{recipe.title}</h3>
                  {recipe.description && (
                    <p className="recipe-card-description">{recipe.description}</p>
                  )}
                  <div className="recipe-card-meta">
                    {recipe.totalTime && (
                      <span><FiClock /> {formatTime(recipe.totalTime)}</span>
                    )}
                    {recipe.servings && (
                      <span><FiUsers /> {recipe.servings}</span>
                    )}
                  </div>
                  <span className="recipe-card-author">
                    by {recipe.createdByMember?.fullName}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// RECIPE FORM (Create / Edit)
// ============================================
const RecipeForm = ({ recipe, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: recipe?.title || '',
    description: recipe?.description || '',
    ingredients: recipe?.ingredients || '',
    instructions: recipe?.instructions || '',
    prepTime: recipe?.prepTime || '',
    cookTime: recipe?.cookTime || '',
    servings: recipe?.servings || '',
    category: recipe?.category || '',
    originStory: recipe?.originStory || '',
    visibility: recipe?.visibility || 'family'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.ingredients.trim() || !formData.instructions.trim()) {
      setError('Title, ingredients, and instructions are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        prepTime: formData.prepTime ? parseInt(formData.prepTime) : null,
        cookTime: formData.cookTime ? parseInt(formData.cookTime) : null,
        servings: formData.servings ? parseInt(formData.servings) : null
      };

      let response;
      if (recipe) {
        response = await recipeService.updateRecipe(recipe.recipeID, payload);
      } else {
        response = await recipeService.createRecipe(payload);
      }

      if (response.success) {
        onSave(response.data);
      } else {
        setError(response.message || 'Failed to save recipe.');
      }
    } catch (err) {
      setError('Failed to save recipe. Please try again.');
      console.error('Failed to save recipe:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="recipe-form card">
      <div className="card-body">
        <h2>{recipe ? 'Edit Recipe' : 'Add New Recipe'}</h2>

        {error && <div className="form-error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Recipe Title *</label>
            <input
              type="text"
              name="title"
              className="form-input"
              placeholder="e.g., Grandma's Apple Pie"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-input"
              rows={2}
              placeholder="A short description of this recipe..."
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Prep Time (minutes)</label>
              <input
                type="number"
                name="prepTime"
                className="form-input"
                placeholder="e.g., 15"
                value={formData.prepTime}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Cook Time (minutes)</label>
              <input
                type="number"
                name="cookTime"
                className="form-input"
                placeholder="e.g., 45"
                value={formData.cookTime}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Servings</label>
              <input
                type="number"
                name="servings"
                className="form-input"
                placeholder="e.g., 8"
                value={formData.servings}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Ingredients * (one per line)</label>
            <textarea
              name="ingredients"
              className="form-input"
              rows={8}
              placeholder={"2 cups flour\n1 cup sugar\n3 large eggs\n1 tsp vanilla extract"}
              value={formData.ingredients}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Instructions * (one step per line)</label>
            <textarea
              name="instructions"
              className="form-input"
              rows={8}
              placeholder={"Preheat oven to 350°F\nMix dry ingredients together\nAdd wet ingredients and stir\nPour into pan and bake for 45 minutes"}
              value={formData.instructions}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Origin Story</label>
            <textarea
              name="originStory"
              className="form-input"
              rows={3}
              placeholder="Where did this recipe come from? Any family memories associated with it?"
              value={formData.originStory}
              onChange={handleChange}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (recipe ? 'Update Recipe' : 'Add Recipe')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Recipes;